import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { apiClientWH } from '@shared/services';
import React from 'react';
import { ProductsApiAdapter } from '../../adapters/ProductsApiAdapter';

interface CreateUpdateButtonProps {
  nombre: string;
  descripcion: string;
  codigo: string;
  coste: string;
  categoria: string;
  imageFile: File | null;
  length: string;
  heigth: string;
  width: string;
  weight: string;
  enlaces: Array<any>;
  stocks: Array<any>;
  productId?: string | number;
  queryClient: any;
  navigate: (path: string) => void;
  t: (key: string, defaultValue?: string) => string;
  toast: (opts: { title: string; description: string; variant: string }) => void;
  weightUnit: string;
  dimensionUnit: string;
  batchActive: boolean;
  batchType: string;
  tenantChildId?: number | null;
  disabled?: boolean;
  /** Carga del producto (GET) al editar; usado para enviar solo campos modificados */
  initialProductData?: any;
  /** Tras guardar en edición: actualizar datos base sin salir de la página */
  onEditSuccess?: () => void | Promise<void>;
}

export const CreateUpdateButton: React.FC<CreateUpdateButtonProps> = ({
  nombre,
  descripcion,
  codigo,
  coste,
  categoria,
  imageFile,
  length,
  heigth,
  width,
  weight,
  enlaces,
  stocks,
  productId,
  queryClient,
  navigate,
  t,
  toast,
  weightUnit,
  dimensionUnit,
  batchActive,
  batchType,
  tenantChildId,
  disabled,
  initialProductData,
  onEditSuccess,
}) => {
  const handleCreateOrUpdate = async () => {
    console.log('Validando:', { nombre, codigo });
    if (!nombre || !codigo) {
      toast({
        title: t('form.error_title'),
        description: t('form.error_required'),
        variant: 'destructive',
      });
      return;
    }
    try {
      const isEdit = Boolean(productId);
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ProductsApiAdapter(httpClient);
      // Guardar producto (crear o actualizar)
      const payload = {
        name: nombre,
        description: descripcion,
        code: codigo,
        cost: Number(coste),
        categoryId: categoria,
        imageFile: imageFile || undefined,
        length,
        heigth,
        width,
        weight,
        weight_unit: weightUnit,
        dimension_unit: dimensionUnit,
        batch_active: batchActive,
        batch_type: batchActive ? batchType : undefined,
        ...(!isEdit && { tenantChildId }), // Solo incluir tenantChildId al crear
      };
      const productResp = isEdit
        ? await adapter.updateProduct(productId, payload, initialProductData)
        : await adapter.createProduct(payload);
      const newProductId = productResp?.id || productId;
      console.log('Producto creado, id:', newProductId);
      // Guardar enlaces (solo si hay)
      console.log('Enlaces a guardar:', enlaces);
      if (enlaces && enlaces.length > 0 && newProductId) {
        for (const enlace of enlaces) {
          // Extraer los ids correctos
          const shopId = Number(enlace.tienda?.value || enlace.tienda?.id);
          const externalProductId = Number(enlace.producto?.value || enlace.producto?.id);
          const externalVariantId = enlace.variante?.value || enlace.variante?.id;
          const productoObj = enlace.producto;
          const numericProductId = Number(newProductId);
          if (shopId && externalProductId) {
            const linkPayload: any = {
              productId: numericProductId,
              externalProductId,
              shopId,
            };

            // Si hay variante seleccionada, enviar externalVariantId
            if (externalVariantId) {
              linkPayload.externalVariantId = Number(externalVariantId);
            }
            // Si no hay variante pero el producto tiene variant_id, enviarlo como externalVariantId
            else if (productoObj?.variant_id) {
              linkPayload.externalVariantId = Number(productoObj.variant_id);
            }

            await adapter.createLink(linkPayload);
          } else {
            console.warn('Enlace incompleto, no se envía:', enlace);
          }
        }
      }
      // Guardar stock (solo si hay)
      if (stocks && stocks.length > 0 && newProductId) {
        console.log('Guardando stocks:', stocks, 'para producto:', newProductId);
        for (const stock of stocks) {
          const locationId = stock.ubicacionId;
          const quantity = stock.stock;
          const description = stock.motivo;
          if (locationId && quantity) {
            await adapter.adjustStock({
              locationId,
              productId: newProductId,
              quantity,
              description,
            });
            console.log('Stock ajustado para ubicación', locationId);
          }
        }
      }
      toast({
        title: isEdit ? t('form.edit_success_title') : t('form.success_title'),
        description: isEdit ? t('form.edit_success_desc') : t('form.success_desc'),
        variant: 'success',
      });

      // Invalidar queries para refrescar la lista de productos
      await queryClient.invalidateQueries({ queryKey: ['products'] });

      if (isEdit) {
        await onEditSuccess?.();
      } else {
        navigate('/products');
      }
    } catch (error: any) {
      console.log('ERROR API:', error);
      const normalizeError = (err: any): string => {
        const data = err?.response?.data || err?.data;
        if (data) {
          // 1) Mensaje directo en data.error.message o data.message
          const directMessage = data?.error?.message || data?.message;
          if (typeof directMessage === 'string' && directMessage.trim().length > 0) {
            return directMessage;
          }
          // 2) Errores de validación por campos
          if (data.errors && typeof data.errors === 'object') {
            const lines = Object.entries(data.errors).map(([field, msgs]) => {
              if (Array.isArray(msgs)) return `${field}: ${msgs.join(', ')}`;
              if (typeof msgs === 'string') return `${field}: ${msgs}`;
              if (msgs && typeof msgs === 'object')
                return `${field}: ${Object.values(msgs as Record<string, unknown>)
                  .map((v) => String(v))
                  .join(', ')}`;
              return String(field);
            });
            if (lines.length > 0) return lines.join('\n');
          }
          // 3) Si message viene como JSON string, intentar parsear
          if (typeof data === 'string') {
            const trimmed = data.trim();
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
              try {
                const parsed = JSON.parse(trimmed);
                return normalizeError({ data: parsed });
              } catch {
                return trimmed;
              }
            }
          }
        }
        // 4) Fallback al mensaje del error nativo
        if (typeof err?.message === 'string' && err.message) return err.message;
        return t('form.error_desc');
      };

      toast({
        title: t('form.error_title'),
        description: normalizeError(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <Button
      variant="primary"
      size="lg"
      className="shrink-0 "
      data-testid="create-product-button"
      onClick={handleCreateOrUpdate}
      disabled={disabled}
    >
      {productId ? t('buttons.save') : t('buttons.create')}
    </Button>
  );
};
